import {
  BookOpen, Download, ExternalLink, PlayCircle, CheckCircle2,
} from 'lucide-react';
import { GROUPS, startStaffTour, openStaffGuide, downloadStaffGuidePdf } from '../lib/staffTour';

export default function UserGuide() {
  const scrollTo = (id) => {
    const el = document.getElementById(`guide-${id}`);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div className="p-5 lg:p-8 max-w-6xl mx-auto" data-testid="user-guide-page">
      {/* Header */}
      <div className="rounded-2xl bg-[#0B2942] text-[#ffffff] p-6 lg:p-8 mb-6">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 text-teal-300 text-xs font-semibold uppercase tracking-wide mb-2">
              <BookOpen className="w-4 h-4" /> Staff guide
            </div>
            <h1 className="font-heading text-2xl lg:text-3xl font-extrabold text-[#ffffff]">How to use the CRM</h1>
            <p className="text-[#ffffff]/80 mt-2 text-sm lg:text-base">
              A full walkthrough for brand-new team members. Take the interactive tour to see where everything
              lives, then use the sections below for step-by-step help on each tool.
            </p>
            <div className="flex flex-wrap items-center gap-2.5 mt-5">
              <button onClick={() => startStaffTour()} data-testid="start-tour-btn"
                className="inline-flex items-center gap-2 bg-[#00A79D] hover:bg-[#00938a] text-[#ffffff] font-semibold px-5 py-2.5 rounded-lg transition-colors">
                <PlayCircle className="w-5 h-5" /> Start interactive tour
              </button>
              <button onClick={() => downloadStaffGuidePdf()} data-testid="guide-download-pdf"
                className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 text-[#ffffff] font-semibold px-4 py-2.5 rounded-lg transition-colors">
                <Download className="w-4 h-4" /> Download PDF
              </button>
              <button onClick={() => openStaffGuide()} data-testid="guide-open-tab"
                className="inline-flex items-center gap-2 text-[#ffffff]/80 hover:text-[#ffffff] font-semibold px-3 py-2.5 rounded-lg transition-colors">
                <ExternalLink className="w-4 h-4" /> Open full guide
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="grid lg:grid-cols-[220px_1fr] gap-8">
        {/* Table of contents */}
        <aside className="hidden lg:block">
          <div className="sticky top-6 space-y-5" data-testid="guide-toc">
            {GROUPS.map((g) => (
              <div key={g.id}>
                <p className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-gray-400 mb-1.5">
                  <g.icon className="w-3.5 h-3.5" /> {g.title}
                </p>
                <ul className="space-y-1">
                  {g.sections.map((s) => (
                    <li key={s.id}>
                      <button onClick={() => scrollTo(s.id)} data-testid={`toc-${s.id}`}
                        className="text-sm text-gray-600 hover:text-[#00857D] text-left transition-colors">
                        {s.title}
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </aside>

        {/* Content */}
        <div className="space-y-12 min-w-0">
          {GROUPS.map((g) => (
            <section key={g.id} data-testid={`guide-group-${g.id}`}>
              <div className="flex items-center gap-2.5 mb-1">
                <span className="inline-flex items-center justify-center w-9 h-9 rounded-xl bg-[#00A79D]/10 text-[#00857D]"><g.icon className="w-5 h-5" /></span>
                <h2 className="font-heading text-xl font-bold text-gray-900">{g.title}</h2>
              </div>
              <p className="text-sm text-gray-500 mb-5">{g.blurb}</p>

              <div className="space-y-5">
                {g.sections.map((s) => (
                  <div key={s.id} id={`guide-${s.id}`} data-testid={`guide-section-${s.id}`}
                    className="scroll-mt-6 bg-white border border-gray-200 rounded-xl p-5">
                    <h3 className="font-heading text-base font-semibold text-gray-900">{s.title}</h3>
                    <p className="text-sm text-gray-600 mt-1.5 leading-relaxed">{s.what}</p>

                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mt-4 mb-2">How to</p>
                    <ol className="space-y-1.5">
                      {s.steps.map((step, i) => (
                        <li key={i} className="flex items-start gap-2 text-sm text-gray-700">
                          <span className="inline-flex items-center justify-center w-5 h-5 shrink-0 rounded-full bg-[#00A79D]/10 text-[#00857D] text-[11px] font-bold mt-0.5">{i + 1}</span>
                          <span>{step}</span>
                        </li>
                      ))}
                    </ol>
                  </div>
                ))}
              </div>
            </section>
          ))}

          <div className="flex items-center gap-2 text-sm text-gray-500 border-t border-gray-200 pt-6">
            <CheckCircle2 className="w-4 h-4 text-[#00A79D]" />
            New to the team? Run the interactive tour above first, then keep this page open as you work.
          </div>
        </div>
      </div>
    </div>
  );
}
